/**
 * Wishlist Utility
 * Manages wishlist functionality using localStorage
 */

class WishlistManager {
  constructor() {
    this.storageKey = "nycd_wishlist";
    this.init();
  }

  init() {
    // Update wishlist count on page load
    this.updateWishlistCount();
    // Listen for storage changes (for cross-tab updates)
    window.addEventListener("storage", () => {
      this.updateWishlistCount();
      this.updateWishlistButtons();
    });
    // Update buttons on page load
    this.updateWishlistButtons();
  }

  /**
   * Get all wishlist items
   * @returns {Array} Array of product handles
   */
  getItems() {
    try {
      const items = localStorage.getItem(this.storageKey);
      return items ? JSON.parse(items) : [];
    } catch (error) {
      console.error("Error reading wishlist:", error);
      return [];
    }
  }

  /**
   * Save wishlist items to localStorage
   * @param {Array} items - Array of product handles
   */
  saveItems(items) {
    try {
      localStorage.setItem(this.storageKey, JSON.stringify(items));
      this.updateWishlistCount();
      this.updateWishlistButtons();
      // Dispatch custom event for other components
      window.dispatchEvent(
        new CustomEvent("wishlist-updated", { detail: { items } })
      );
    } catch (error) {
      console.error("Error saving wishlist:", error);
    }
  }

  /**
   * Check if product is in wishlist
   * @param {string} handle - Product handle
   * @returns {boolean}
   */
  isInWishlist(handle) {
    const items = this.getItems();
    return items.includes(handle);
  }

  /**
   * Add product to wishlist
   * @param {string} handle - Product handle
   * @returns {boolean} Success status
   */
  add(handle) {
    if (!handle) return false;
    const items = this.getItems();
    if (!items.includes(handle)) {
      items.push(handle);
      this.saveItems(items);
      return true;
    }
    return false;
  }

  /**
   * Remove product from wishlist
   * @param {string} handle - Product handle
   * @returns {boolean} Success status
   */
  remove(handle) {
    if (!handle) return false;
    const items = this.getItems();
    const index = items.indexOf(handle);
    if (index > -1) {
      items.splice(index, 1);
      this.saveItems(items);
      return true;
    }
    return false;
  }

  /**
   * Toggle product in wishlist
   * @param {string} handle - Product handle
   * @returns {boolean} True if added, false if removed
   */
  toggle(handle) {
    if (this.isInWishlist(handle)) {
      this.remove(handle);
      return false;
    } else {
      this.add(handle);
      return true;
    }
  }

  /**
   * Get wishlist count
   * @returns {number}
   */
  getCount() {
    return this.getItems().length;
  }

  /**
   * Clear wishlist
   */
  clear() {
    this.saveItems([]);
  }

  /**
   * Update wishlist count badge in header
   */
  updateWishlistCount() {
    const count = this.getCount();
    const badge = document.getElementById("wishlist-count-badge");
    const headerLink = document.getElementById("header_menu_wishlist");

    if (badge) {
      if (count > 0) {
        badge.textContent = count;
        badge.classList.remove("hidden");
      } else {
        badge.classList.add("hidden");
      }
    }

    // Also update any other wishlist count elements
    document.querySelectorAll("[data-wishlist-count]").forEach((el) => {
      el.textContent = count;
      el.classList.toggle("hidden", count === 0);
    });
  }

  /**
   * Update wishlist button states on the page
   */
  updateWishlistButtons() {
    document.querySelectorAll("[data-wishlist-handle]").forEach((button) => {
      const handle = button.getAttribute("data-wishlist-handle");
      if (!handle) return;

      const isInWishlist = this.isInWishlist(handle);
      const svg = button.querySelector("svg");
      const path = svg?.querySelector("path");

      if (path) {
        if (isInWishlist) {
          // Filled heart (active state)
          path.setAttribute("fill", "currentColor");
          path.setAttribute("fill-opacity", "1");
          button.classList.add("wishlist-active");
          button.setAttribute("aria-label", "Remove from wishlist");
        } else {
          // Outline heart (inactive state)
          path.setAttribute("fill", "black");
          path.setAttribute("fill-opacity", "0.24");
          button.classList.remove("wishlist-active");
          button.setAttribute("aria-label", "Add to wishlist");
        }
      }
    });
  }
}

// Initialize wishlist manager
const wishlistManager = new WishlistManager();

// Export for use in other scripts
if (typeof window !== "undefined") {
  window.wishlistManager = wishlistManager;
}

// Handle wishlist button clicks
document.addEventListener("click", (e) => {
  const button = e.target.closest("[data-wishlist-handle]");
  if (!button) return;

  e.preventDefault();
  e.stopPropagation();

  const handle = button.getAttribute("data-wishlist-handle");
  if (!handle) return;

  const wasAdded = wishlistManager.toggle(handle);

  // Visual feedback
  button.classList.add("wishlist-animating");
  setTimeout(() => {
    button.classList.remove("wishlist-animating");
  }, 300);

  // Optional: Show toast notification
  if (typeof window.showToast === "function") {
    window.showToast(wasAdded ? "Added to wishlist" : "Removed from wishlist");
  }
});

// Listen for wishlist updates
window.addEventListener("wishlist-updated", () => {
  wishlistManager.updateWishlistButtons();
  wishlistManager.updateWishlistCount();
});
